import { ColumnType, ColumnGroupType } from '@webthatmatters/orca-table';
import { AiAnalyticsField, AiAnalyticsResponse } from '@/types/ai-logs';
import InvoiceFieldNameSelector from '@/components/selectors/InvoiceFieldNameSelector';
import DateFilterSelector from '@/common/components/table/filters/DateFilterSelector';
import { numberToStr } from '@/library/utils/numbers';
import TextWithTooltip from '@/library/components/general/TextWithTooltip';

export type AiFieldOption = { value: string; label: string };

type TableConfigOptions = {
  entityLabel: string;
  fieldOptions: AiFieldOption[];
};

const METRIC_COLUMN_HEADER_PROPS = {
  headerClassName: 'text-end overflow-hidden',
  headerInnerClassName: 'd-flex align-items-end justify-content-end fs-10 fw-bold min-w-0 w-100p'
} as const;

const METRIC_COLUMN_HEADER_TOTALS_CLASSNAME = 'text-end';

export const getFieldLevelMetricsGroup = (
  apiTotals: AiAnalyticsResponse | null,
  { entityLabel }: Pick<TableConfigOptions, 'entityLabel'>
): ColumnGroupType<AiAnalyticsField> => ({
  header: () => <span className="fw-bold">Field-Level Metrics</span>,
  groupKey: 'field_level_metrics',
  columns: [
    {
      header: 'Attempts',
      infoMessage: `Number of times the AI attempted to extract this field from ${entityLabel}.`,
      key: 'total_rows',
      size: 115,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      headerTotals: () => {
        return (
          <strong className="text-end fs-12">
            {numberToStr(apiTotals?.total_field_evaluations)}
          </strong>
        );
      }
    },
    {
      header: 'AI Detected',
      infoMessage: 'Number of times the AI found a value for this field, regardless of accuracy.',
      key: 'ai_value_predicted_count',
      size: 140,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      headerTotals: () => {
        return (
          <strong className="text-end fs-12">
            {numberToStr(apiTotals?.total_ai_value_predicted_count)}
          </strong>
        );
      }
    },
    {
      header: 'AI Correct',
      infoMessage:
        "Number of times the AI's prediction exactly matched the final user-approved value.",
      key: 'ai_and_user_value_match_count',
      size: 130,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      headerTotals: () => {
        return (
          <strong className="text-end fs-12">
            {numberToStr(apiTotals?.total_ai_and_user_value_match_count)}
          </strong>
        );
      }
    },
    {
      header: 'User Corrections',
      infoMessage:
        "Number of times users manually modified the AI's prediction, indicating the AI value was incorrect.",
      key: 'user_correction_count',
      size: 170,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      headerTotals: () => {
        return (
          <strong className="text-end fs-12">
            {numberToStr(apiTotals?.total_user_correction_count)}
          </strong>
        );
      }
    },
    {
      header: 'Prediction Rate',
      infoMessage: 'Fraction of attempts where the AI provided a value',
      key: 'ai_prediction_rate_on_user_value_set',
      size: 156,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      row: ({ row }) => {
        const value = row.original.ai_prediction_rate_on_user_value_set;
        return value !== null ? `${(value * 100).toFixed(2)}%` : 'N/A';
      }
    }
  ]
});

export const getBenchmarksGroup = (
  apiTotals: AiAnalyticsResponse | null,
  { entityLabel }: Pick<TableConfigOptions, 'entityLabel'>
): ColumnGroupType<AiAnalyticsField> => ({
  header: () => <span className="fw-bold">Benchmarks</span>,
  groupKey: 'benchmarks',
  columns: [
    {
      header: 'User Filled',
      infoMessage: `Number of ${entityLabel} where users provided a value for this field (either kept AI value or entered manually).`,
      key: 'user_value_set_count',
      size: 130,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      headerTotals: () => {
        return (
          <strong className="text-end fs-12">
            {numberToStr(apiTotals?.total_user_value_set_count)}
          </strong>
        );
      }
    },
    {
      header: 'User Empty',
      infoMessage: 'Field was intentionally left blank.',
      key: 'user_value_not_set_count',
      size: 130,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      headerTotals: () => {
        return (
          <strong className="text-end fs-12">
            {numberToStr(apiTotals?.total_user_value_not_set_count)}
          </strong>
        );
      }
    },
    {
      header: 'Both Filled',
      infoMessage: `Number of ${entityLabel} where both AI and user provided values for this field, enabling direct comparison.`,
      key: 'user_and_ai_value_set_count',
      size: 140,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      headerTotals: () => {
        return (
          <strong className="text-end fs-12">
            {numberToStr(apiTotals?.total_user_and_ai_value_set_count)}
          </strong>
        );
      }
    },
    {
      header: () => <span className="fw-bold text-start">Accuracy (When User Set)</span>,
      infoMessage:
        'AI accuracy rate specifically for cases where users filled in a value. Shows how well AI performs on fields that actually matter to users.',
      key: 'accuracy_on_user_value_set',
      size: 130,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      row: ({ row }) => {
        const value = row.original.accuracy_on_user_value_set;
        return value !== null ? `${(value * 100).toFixed(2)}%` : 'N/A';
      }
    },
    {
      header: 'Precision',
      infoMessage:
        'Of all the values the AI predicted, what percentage were correct? High precision means fewer false positives.',
      key: 'avg_precision',
      size: 110,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      row: ({ row }) => {
        const value = row.original.avg_precision;
        return value !== null ? `${(value * 100).toFixed(2)}%` : 'N/A';
      }
    },
    {
      header: 'Recall',
      infoMessage:
        'Of all the values users entered, what percentage did the AI correctly predict? High recall means the AI catches most required fields.',
      key: 'avg_recall',
      size: 110,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      row: ({ row }) => {
        const value = row.original.avg_recall;
        return value !== null ? `${(value * 100).toFixed(2)}%` : 'N/A';
      }
    },
    {
      header: 'F1 Score',
      key: 'f1_score',
      infoMessage:
        'Harmonic mean of Precision and Recall, providing a balanced measure of AI performance. Ranges from 0 (worst) to 1 (perfect).',
      size: 115,
      canSort: true,
      canFilter: false,
      className: 'text-end',
      ...METRIC_COLUMN_HEADER_PROPS,
      headerTotalsClassName: METRIC_COLUMN_HEADER_TOTALS_CLASSNAME,
      row: ({ row }) => {
        const value = row.original.f1_score;
        return value !== null ? `${(value * 100).toFixed(2)}%` : 'N/A';
      }
    }
  ]
});

export const getColumns = (
  fieldLevelMetricsGroup: ColumnGroupType<AiAnalyticsField>,
  benchmarksGroup: ColumnGroupType<AiAnalyticsField>,
  { entityLabel, fieldOptions }: TableConfigOptions
): ColumnType<AiAnalyticsField>[] => [
  {
    header: 'Field Name',
    key: 'fields',
    component: InvoiceFieldNameSelector,
    componentRest: { options: fieldOptions },
    hidden: true,
    canFilter: true,
    size: 100,
    type: 'collection'
  },
  {
    header: 'Start Date',
    key: 'start-date',
    hidden: true,
    canFilter: true,
    component: DateFilterSelector,
    type: 'date'
  },
  {
    header: 'End Date',
    key: 'end-date',
    hidden: true,
    canFilter: true,
    component: DateFilterSelector,
    type: 'date'
  },
  {
    header: 'Field Name',
    infoMessage: `The name of the ${entityLabel.slice(0, -1)} field being analyzed.`,
    key: 'field_name',
    size: 150,
    canSort: true,
    isSticky: true,
    canFilter: false,
    headerInnerClassName: 'fw-bold',
    headerTotals: 'Totals',
    isHandlingCompactView: true,
    row: ({ row, table }) => {
      const compactViewEnabled = table.getState().isCompactViewEnabled;
      return (
        <TextWithTooltip allowOverflow={!compactViewEnabled}>
          <span className="capitalize">{row.original.display_name}</span>
        </TextWithTooltip>
      );
    }
  },
  fieldLevelMetricsGroup,
  benchmarksGroup
];
